import { useEffect, useRef } from "react";

export function BadgeFilterCanvas({
  baseImageUrl,
  filterImageUrl,
  opacity = 0.5,
  effect = "none",
  size = 400,
}: {
  baseImageUrl: string;
  filterImageUrl: string;
  opacity?: number;
  effect?: string;
  size?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const baseImg = new Image();
    const filterImg = new Image();

    // Try loading with anonymous crossOrigin to allow pixel manipulation if the server supports it
    baseImg.crossOrigin = "anonymous";
    filterImg.crossOrigin = "anonymous";

    let animationFrameId: number;
    const sparkles: {
      x: number;
      y: number;
      size: number;
      phase: number;
      speed: number;
    }[] = [];

    if (effect === "sparkles") {
      for (let i = 0; i < 20; i++) {
        sparkles.push({
          x: Math.random() * size,
          y: Math.random() * size,
          size: (size / 400) * (4 + Math.random() * 6),
          phase: Math.random() * Math.PI * 2,
          speed: 0.02 + Math.random() * 0.04,
        });
      }
    }

    const loadImage = (img: HTMLImageElement, url: string) => {
      return new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => {
          // If loading with crossOrigin fails, try loading without it as a fallback.
          // This will "taint" the canvas and prevent pixel manipulation, but at least
          // the image will be visible.
          if (img.crossOrigin === "anonymous") {
            img.removeAttribute("crossOrigin");
            img.src = url;
          } else {
            resolve();
          }
        };
        img.src = url;
      });
    };

    Promise.all([
      loadImage(baseImg, baseImageUrl),
      loadImage(filterImg, filterImageUrl),
    ]).then(() => {
      const offscreen = document.createElement("canvas");
      offscreen.width = size;
      offscreen.height = size;
      const offCtx = offscreen.getContext("2d")!;

      offCtx.drawImage(baseImg, 0, 0, size, size);
      let baseImageData: ImageData;

      try {
        baseImageData = offCtx.getImageData(0, 0, size, size);
        const basePixels = baseImageData.data;

        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = size;
        tempCanvas.height = size;
        const tempCtx = tempCanvas.getContext("2d")!;
        tempCtx.drawImage(filterImg, 0, 0, size, size);
        const filterPixels = tempCtx.getImageData(0, 0, size, size).data;

        for (let i = 0; i < basePixels.length; i += 4) {
          // Skip fully transparent badge pixels
          if (basePixels[i + 3] === 0) continue;
          // Skip fully transparent filter pixels
          if (filterPixels[i + 3] === 0) continue;

          const r1 = basePixels[i],
            g1 = basePixels[i + 1],
            b1 = basePixels[i + 2];
          const r2 = filterPixels[i],
            g2 = filterPixels[i + 1],
            b2 = filterPixels[i + 2];

          const lumBase = 0.299 * r1 + 0.587 * g1 + 0.114 * b1;
          const lumFilter = 0.299 * r2 + 0.587 * g2 + 0.114 * b2;

          if (lumFilter > lumBase) {
            // Factor the filter image's alpha channel into the opacity calculation for smoother blending
            const filterAlpha = filterPixels[i + 3] / 255;
            const finalOpacity = opacity * filterAlpha;

            basePixels[i] = r2 * finalOpacity + r1 * (1 - finalOpacity);
            basePixels[i + 1] = g2 * finalOpacity + g1 * (1 - finalOpacity);
            basePixels[i + 2] = b2 * finalOpacity + b1 * (1 - finalOpacity);
            // Note: basePixels[i + 3] is deliberately unmodified to preserve the badge's alpha mask
          }
        }
        offCtx.putImageData(baseImageData, 0, 0);
      } catch {
        // Fallback for CORS errors: using source-atop forces the filter to respect base image transparency
        offCtx.globalCompositeOperation = "source-atop";
        offCtx.globalAlpha = opacity;
        offCtx.drawImage(filterImg, 0, 0, size, size);
        offCtx.globalAlpha = 1.0;
        offCtx.globalCompositeOperation = "source-over";
      }

      const animate = () => {
        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(offscreen, 0, 0);

        if (effect === "sparkles") {
          // Inside the animation loop, wrap the sparkle drawing logic with source-atop
          // so sparkles don't bleed into the transparent background.
          ctx.globalCompositeOperation = "source-atop";

          sparkles.forEach((s) => {
            s.phase += s.speed;
            const sOpacity = (Math.sin(s.phase) + 1) / 2;
            const r = s.size;

            ctx.beginPath();
            ctx.moveTo(s.x - r, s.y);
            ctx.lineTo(s.x + r, s.y);
            ctx.moveTo(s.x, s.y - r);
            ctx.lineTo(s.x, s.y + r);
            ctx.strokeStyle = `rgba(255, 255, 255, ${sOpacity * 0.9})`;
            ctx.lineWidth = Math.max(0.5, (size / 400) * 1.5);
            ctx.stroke();

            ctx.beginPath();
            ctx.arc(s.x, s.y, Math.max(0.2, (size / 400) * 1), 0, Math.PI * 2);
            ctx.fillStyle = "white";
            ctx.fill();
          });

          // Restore normal rendering for the next frame
          ctx.globalCompositeOperation = "source-over";
          animationFrameId = requestAnimationFrame(animate);
        }
      };

      if (effect === "sparkles") {
        animate();
      } else {
        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(offscreen, 0, 0);
      }
    });

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [baseImageUrl, filterImageUrl, opacity, effect, size]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      className="rounded object-cover border shrink-0"
      style={{ width: size, height: size }}
    />
  );
}

export function BadgeFilterPreviewCanvas({
  baseImageUrl,
  filterImageUrl,
  opacity = 0.5,
  effect = "none",
  size = 400,
}: {
  baseImageUrl: string;
  filterImageUrl: string;
  opacity?: number;
  effect?: string;
  size?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const baseImg = new Image();
    const filterImg = new Image();

    // Try loading with anonymous crossOrigin to allow pixel manipulation if the server supports it
    baseImg.crossOrigin = "anonymous";
    filterImg.crossOrigin = "anonymous";

    let animationFrameId: number;
    const sparkles: {
      x: number;
      y: number;
      size: number;
      phase: number;
      speed: number;
    }[] = [];

    if (effect === "sparkles") {
      for (let i = 0; i < 20; i++) {
        sparkles.push({
          x: Math.random() * size,
          y: Math.random() * size,
          size: (size / 400) * (4 + Math.random() * 6),
          phase: Math.random() * Math.PI * 2,
          speed: 0.02 + Math.random() * 0.04,
        });
      }
    }

    const loadImage = (img: HTMLImageElement, url: string) => {
      return new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => {
          // If loading with crossOrigin fails, try loading without it as a fallback.
          // This will "taint" the canvas and prevent pixel manipulation, but at least
          // the image will be visible.
          if (img.crossOrigin === "anonymous") {
            img.removeAttribute("crossOrigin");
            img.src = url;
          } else {
            resolve();
          }
        };
        img.src = url;
      });
    };

    Promise.all([
      loadImage(baseImg, baseImageUrl),
      loadImage(filterImg, filterImageUrl),
    ]).then(() => {
      const offscreen = document.createElement("canvas");
      offscreen.width = size;
      offscreen.height = size;
      const offCtx = offscreen.getContext("2d")!;

      offCtx.drawImage(baseImg, 0, 0, size, size);
      let baseImageData: ImageData;

      try {
        baseImageData = offCtx.getImageData(0, 0, size, size);
        const basePixels = baseImageData.data;

        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = size;
        tempCanvas.height = size;
        const tempCtx = tempCanvas.getContext("2d")!;
        tempCtx.drawImage(filterImg, 0, 0, size, size);
        const filterPixels = tempCtx.getImageData(0, 0, size, size).data;

        for (let i = 0; i < basePixels.length; i += 4) {
          // Skip fully transparent badge pixels
          if (basePixels[i + 3] === 0) continue;
          // Skip fully transparent filter pixels
          if (filterPixels[i + 3] === 0) continue;

          const r1 = basePixels[i],
            g1 = basePixels[i + 1],
            b1 = basePixels[i + 2];
          const r2 = filterPixels[i],
            g2 = filterPixels[i + 1],
            b2 = filterPixels[i + 2];

          const lumBase = 0.299 * r1 + 0.587 * g1 + 0.114 * b1;
          const lumFilter = 0.299 * r2 + 0.587 * g2 + 0.114 * b2;

          if (lumFilter > lumBase) {
            // Include the filter's alpha in our opacity calculation for smoother blending
            const filterAlpha = filterPixels[i + 3] / 255;
            const finalOpacity = opacity * filterAlpha;

            basePixels[i] = r2 * finalOpacity + r1 * (1 - finalOpacity);
            basePixels[i + 1] = g2 * finalOpacity + g1 * (1 - finalOpacity);
            basePixels[i + 2] = b2 * finalOpacity + b1 * (1 - finalOpacity);
            // Note: basePixels[i + 3] is deliberately unmodified to preserve the badge's alpha mask
          }
        }
        offCtx.putImageData(baseImageData, 0, 0);
      } catch {
        // Fallback for CORS errors: using source-atop forces the filter to respect base image transparency
        offCtx.globalCompositeOperation = "source-atop";
        offCtx.globalAlpha = opacity;
        offCtx.drawImage(filterImg, 0, 0, size, size);
        offCtx.globalAlpha = 1.0;
        offCtx.globalCompositeOperation = "source-over";
      }

      const animate = () => {
        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(offscreen, 0, 0);

        if (effect === "sparkles") {
          // Mask the sparkles so they only render over non-transparent pixels
          ctx.globalCompositeOperation = "source-atop";

          sparkles.forEach((s) => {
            s.phase += s.speed;
            const sOpacity = (Math.sin(s.phase) + 1) / 2;
            const r = s.size;

            ctx.beginPath();
            ctx.moveTo(s.x - r, s.y);
            ctx.lineTo(s.x + r, s.y);
            ctx.moveTo(s.x, s.y - r);
            ctx.lineTo(s.x, s.y + r);
            ctx.strokeStyle = `rgba(255, 255, 255, ${sOpacity * 0.9})`;
            ctx.lineWidth = Math.max(0.5, (size / 400) * 1.5);
            ctx.stroke();

            ctx.beginPath();
            ctx.arc(s.x, s.y, Math.max(0.2, (size / 400) * 1), 0, Math.PI * 2);
            ctx.fillStyle = "white";
            ctx.fill();
          });

          // Restore normal rendering for the next frame
          ctx.globalCompositeOperation = "source-over";
          animationFrameId = requestAnimationFrame(animate);
        }
      };

      if (effect === "sparkles") {
        animate();
      } else {
        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(offscreen, 0, 0);
      }
    });

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [baseImageUrl, filterImageUrl, opacity, effect, size]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      className="rounded object-cover border shrink-0"
      style={{ width: size, height: size }}
    />
  );
}
